import { useCallback, useEffect, useMemo, useState } from "react";
import { BASE_SEPOLIA_TOKENS, type TokenInfo } from "../../config";
import type { BaseStoredNote } from "../types";
import { resolveTokenInfo } from "../helpers/tokenCatalog";
import {
  supportedTokenAddresses,
  resolveSupportedToken,
} from "../helpers/supportedTokens";
import { privateTokenPool } from "../../joinsplit-v2/privatePools";

const initialTokens = () =>
  privateTokenPool()
    ? BASE_SEPOLIA_TOKENS.filter((t) => t.isNative)
    : BASE_SEPOLIA_TOKENS;
export function useTokenCatalog() {
  const [depositTokens, setDepositTokens] =
    useState<TokenInfo[]>(initialTokens);
  const [recovered, setRecovered] = useState<TokenInfo[]>([]);
  useEffect(() => {
    let cancelled = false,
      generation = 0;
    const refresh = async () => {
      const run = ++generation;
      try {
        const addresses = await supportedTokenAddresses(true);
        const resolved = await Promise.allSettled(
          addresses.map(resolveTokenInfo),
        );
        if (!cancelled && generation === run)
          setDepositTokens([
            ...BASE_SEPOLIA_TOKENS.filter((t) => t.isNative),
            ...resolved.flatMap((result) =>
              result.status === "fulfilled" ? [result.value] : [],
            ),
          ]);
      } catch {
        if (!cancelled && generation === run) setDepositTokens(initialTokens());
      }
    };
    void refresh();
    const onFocus = () => {
      void refresh();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 60000);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  const discover = useCallback(async (notes: BaseStoredNote[]) => {
    const addresses = new Set(
      notes
        .filter(
          (n) =>
            !n.spent &&
            n.note.leafIndex >= 0 &&
            n.note.poolAddress?.toLowerCase() ===
              privateTokenPool()?.toLowerCase(),
        )
        .map((n) => n.note.tokenData?.tokenAddress)
        .filter((a): a is string => !!a),
    );
    const resolved = await Promise.allSettled(
      [...addresses].map(resolveTokenInfo),
    );
    setRecovered(
      resolved.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      ),
    );
  }, []);
  const tokens = useMemo(
    () => [
      ...new Map(
        [...depositTokens, ...recovered].map((t) => [
          t.address.toLowerCase(),
          t,
        ]),
      ).values(),
    ],
    [depositTokens, recovered],
  );
  return { tokens, depositTokens, resolveSupportedToken, discover };
}
